import { useMemo, useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { toast } from 'components/global/CUK/Toast/Toast'
import EmptyScreen from 'packages/ui/EmptyScreen'
import { useSites } from 'store'

import AuthenticationSummary from './AuthenticationSummary'
import EditSsidModal from './EditSsidModal'
import SsidsTable from './Table'
import { Ssid } from './types'

const SSIDS = gql`
  query WasabiSsids($sf_id: ID!) {
    ssids: wasabiSsids(site_sf_id: $sf_id) {
      id
      name
      auth_method
      enabled
      passphrase
      vlan
    }
  }
`

const UPDATE_SSID = gql`
  mutation UpdateWasabiSsid($id: ID!, $name: String!, $enabled: Boolean!, $passphrase: String) {
    updateWasabiSsid(id: $id, name: $name, enabled: $enabled, passphrase: $passphrase) {
      id
      name
      enabled
      passphrase
    }
  }
`

interface SsidsData {
  ssids: Ssid[]
}

interface SsidsVariables {
  sf_id: string
}

interface EditSsidValues {
  enabled: boolean
  name: string
  passphrase: string
}

export const canEdit = (user) => {
  return user?.admin
    || !user?.roles?.ssid_monitoring_ro
}
const MonitoringSsids = () => {
  const { t } = useTranslation()
  const [{ selected }] = useSites()
  const selectedSiteId = selected[0]?.sf_id
  const [ssidToEdit, setSsidToEdit] = useState<Ssid | null>(null)

  const { data, loading, error, refetch } = useQuery<SsidsData, SsidsVariables>(SSIDS, {
    variables: { sf_id: selectedSiteId || '' },
    skip: selected.length !== 1 || !selectedSiteId,
    fetchPolicy: 'cache-and-network'
  })
  const [updateSsid, { loading: isSaving }] = useMutation(UPDATE_SSID)

  const ssids = useMemo(() => {
    return [...(data?.ssids || [])].sort((firstSsid, secondSsid) => {
      if (firstSsid.enabled !== secondSsid.enabled) {
        return firstSsid.enabled ? -1 : 1
      }

      return firstSsid.name.localeCompare(secondSsid.name)
    })
  }, [data?.ssids])

  const handleEditSsid = (ssid: Ssid) => {
    setSsidToEdit(ssid)
  }

  const handleCloseEditModal = () => {
    if (isSaving) return
    setSsidToEdit(null)
  }

  const handleSaveChanges = async ({ enabled, name, passphrase }: EditSsidValues) => {
    if (!ssidToEdit) return

    const canEditPassphrase = ssidToEdit.auth_method === 'psk'
    const hasNameChanged = name !== ssidToEdit.name
    const hasStatusChanged = enabled !== ssidToEdit.enabled
    const hasPassphraseChanged = canEditPassphrase && passphrase !== (ssidToEdit.passphrase || '')

    if (!hasNameChanged && !hasStatusChanged && !hasPassphraseChanged) {
      handleCloseEditModal()
      return
    }

    try {
      await updateSsid({
        variables: {
          id: ssidToEdit.id,
          name,
          enabled,
          passphrase: canEditPassphrase ? passphrase : null
        }
      })
      await refetch()
      toast.success(t('ssid_monitoring.notifications.update_success'))
      setSsidToEdit(null)
    } catch {
      toast.error(t('ssid_monitoring.notifications.update_error'))
    }
  }

  if (selected.length !== 1 || !selectedSiteId) {
    return (
      <EmptyScreen icon="wifi" iconSize="80px">
        {t('ssid_monitoring.no_site_selected')}
      </EmptyScreen>
    )
  }

  if (error) {
    return (
      <EmptyScreen icon="triangle-exclamation" iconSize="80px">
        {t('ssid_monitoring.error')}
      </EmptyScreen>
    )
  }

  const isInitialLoading = loading && !data

  return (
    <>
      <Stack flex={1} minHeight={0} spacing={2}>
        <Box>
          <Typography sx={{ color: '#464646', fontSize: 16, fontWeight: 600 }}>{t('ssid_monitoring.title')}</Typography>
          <Typography sx={{ color: '#989898', fontSize: 11, fontWeight: 400, mt: 0.5 }}>
            {t('ssid_monitoring.subtitle')}
          </Typography>
        </Box>

        <AuthenticationSummary loading={isInitialLoading} ssids={ssids} />
        <SsidsTable loading={isInitialLoading} ssids={ssids} onEdit={handleEditSsid} />
      </Stack>

      {ssidToEdit && (
        <EditSsidModal
          key={ssidToEdit.id}
          loading={isSaving}
          ssid={ssidToEdit}
          onClose={handleCloseEditModal}
          onSave={handleSaveChanges}
        />
      )}
    </>
  )
}

export default MonitoringSsids
